import axios from 'axios';
import router from '../router';

const axiosInstance = axios.create();
const GET_CACHE_TTL_MS = 2000;
const getResponseCache = new Map();

function stableStringify(value) {
    if (!value || typeof value !== 'object') {
        return String(value || '');
    }
    if (value instanceof URLSearchParams) {
        return value.toString();
    }
    return JSON.stringify(
        Object.keys(value).sort().reduce((acc, key) => {
            acc[key] = value[key];
            return acc;
        }, {})
    );
}

function buildGetCacheKey(config) {
    const method = String(config.method || 'get').toLowerCase();
    if (method !== 'get' || config.cache === false) {
        return null;
    }
    const token = config.headers?.['x-access-token'] || '';
    const apiKey = config.headers?.['x-api-key'] || '';
    return [
        method,
        config.baseURL || '',
        config.url || '',
        stableStringify(config.params),
        token,
        apiKey
    ].join('|');
}

axiosInstance.interceptors.request.use((config) => {
    const cacheKey = buildGetCacheKey(config);
    if (!cacheKey) {
        return config;
    }

    const cached = getResponseCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
        config.adapter = () => Promise.resolve({
            data: cached.data,
            status: cached.status,
            statusText: cached.statusText,
            headers: cached.headers,
            config,
            request: null
        });
        return config;
    }

    config.metadata = {
        ...(config.metadata || {}),
        getCacheKey: cacheKey
    };
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => {
        const cacheKey = response.config?.metadata?.getCacheKey;
        if (cacheKey) {
            getResponseCache.set(cacheKey, {
                data: response.data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                expiresAt: Date.now() + GET_CACHE_TTL_MS
            });
            if (getResponseCache.size > 200) {
                const now = Date.now();
                for (const [key, value] of getResponseCache.entries()) {
                    if (value.expiresAt <= now || getResponseCache.size > 200) {
                        getResponseCache.delete(key);
                    }
                }
            }
        }
        return response;
    },
    (error) => {
        // Axios errors contain request bodies and authentication headers.
        console.warn('API request failed:', {
            status: error.response?.status,
            code: error.code
        });

        if (error.response && error.response.status === 401) {
            console.log('Token expired at:', new Date().toISOString());
            localStorage.removeItem('user');
            localStorage.removeItem('adminUserBeforeImpersonation');

            if (router.currentRoute.value.name !== 'login') {
                router.push({ name: 'login' }).then(() => {
                    alert('Vaša seja je potekla. Prosimo, prijavite se ponovno.');
                    window.location.reload();
                });
            }
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;
